import {useMutation} from "@tanstack/react-query";
import {useApiRequest} from "../../../../../common/hooks/useApiRequest";
import {useSnackbar} from "../../../../../common/context/SnackbarContext";
import {useDeck} from "../../../../deck-management/context/DeckContext";
import {WordWithId} from "../../../../../common/types/words";
import {RequestStatusSnackbarConfig} from "../../../../../common/types/requests";
import {VocabularyActionDispatch} from "../../../pages/Vocabulary";

export interface UseWordDeleteViewArgs {
    word: WordWithId;
    actionDispatch?: VocabularyActionDispatch;
    snackbar?: RequestStatusSnackbarConfig;
}

export const useWordDeleteView = ({word, actionDispatch, snackbar}: UseWordDeleteViewArgs) => {

    const {deleteRequest} = useApiRequest();
    const {showSnackbar} = useSnackbar();
    const {currentDeck} = useDeck();

    const {mutateAsync: executeDeleteFromDeck} = useMutation({
        mutationFn: () => deleteRequest(`decks/${currentDeck!.deckId}/words/${word.wordId}`),
        onSuccess: () => snackbar?.showRequestSuccessSnackbar && showSnackbar("Word deleted successfully!", "success"),
        onError: () => snackbar?.showRequestErrorSnackbar && showSnackbar("Failed to delete word.", "error"),
    });

    const {mutateAsync: executeDeleteFromLanguage} = useMutation({
        mutationFn: () => deleteRequest(`words/${word.wordId}`),
        onSuccess: () => snackbar?.showRequestSuccessSnackbar && showSnackbar("Word deleted successfully!", "success"),
        onError: () => snackbar?.showRequestErrorSnackbar && showSnackbar("Failed to delete word.", "error"),
    });

    const deleteFromDeckButtonClicked = () => {
        snackbar?.showRequestSentSnackbar && showSnackbar("Deleting word...", "info");
        const deletePromise: Promise<string> = executeDeleteFromDeck().then(() => word.wordId);
        !!actionDispatch?.delete && actionDispatch.delete(deletePromise);
    };

    const deleteFromLanguageButtonClicked = () => {
        snackbar?.showRequestSentSnackbar && showSnackbar("Deleting word...", "info");
        const deletePromise: Promise<string> = executeDeleteFromLanguage().then(() => word.wordId);
        !!actionDispatch?.delete && actionDispatch.delete(deletePromise);
    };

    return {
        currentDeck,
        deleteFromDeckButtonClicked,
        deleteFromLanguageButtonClicked,
    };
};
